/**
 * The FSBO savings maths, shared by the server renderer (src/fsbo/fsbo.ts, the default
 * example printed into the HTML) and the browser calculator (src/fsbo/calculator.ts).
 * No imports, so the client bundle stays tiny.
 */

/** Typical total commission on a US sale (listing + buyer's agent), as a range. */
export const COMMISSION_LOW = 0.05;
export const COMMISSION_HIGH = 0.06;
/** The listing agent's half: what an FSBO seller still saves when they pay the buyer's agent. */
export const LISTING_SIDE_LOW = 0.025;
export const LISTING_SIDE_HIGH = 0.03;
/** The sale price the server-rendered example uses (an example, not a median). */
export const EXAMPLE_PRICE = 400_000;
export const MAX_PRICE = 100_000_000;

export interface Savings {
  price: number;
  /** State transfer tax at the flat state rate; null when the rate isn't a single number. */
  transferTax: number | null;
  commissionLow: number;
  commissionHigh: number;
  /** Still saved if the seller pays the buyer's agent (only the listing side is saved). */
  listingSideLow: number;
  listingSideHigh: number;
}

/** ratePercent is a percentage (0.2 = 0.2 %). `hasTax === false` means no state tax: 0. */
export function savings(price: number, ratePercent: number | null, hasTax: boolean | null): Savings {
  const p = Math.max(0, Math.min(MAX_PRICE, Number.isFinite(price) ? price : 0));
  const transferTax = hasTax === false ? 0 : ratePercent === null ? null : (p * ratePercent) / 100;
  return {
    price: p,
    transferTax,
    commissionLow: p * COMMISSION_LOW,
    commissionHigh: p * COMMISSION_HIGH,
    listingSideLow: p * LISTING_SIDE_LOW,
    listingSideHigh: p * LISTING_SIDE_HIGH,
  };
}

const fmtUsd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export function usd(n: number): string {
  return fmtUsd.format(Math.round(n));
}

export function usdRange(a: number, b: number): string {
  return `${usd(a)}–${usd(b)}`;
}

/** "1,250,000" / "$400k" / "400000" → 400000; anything unreadable → NaN. */
export function parsePrice(text: string): number {
  const t = text.trim().toLowerCase().replace(/[$,\s]/g, '');
  const m = t.match(/^(\d+(?:\.\d+)?)(k|m)?$/);
  if (!m) return NaN;
  const n = Number(m[1]);
  return m[2] === 'k' ? n * 1_000 : m[2] === 'm' ? n * 1_000_000 : n;
}

/** 0.2 → "0.2%", 1 → "1%", 0.4625 → "0.463%". */
export function percentText(ratePercent: number): string {
  return `${Number(ratePercent.toFixed(3))}%`;
}
